import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Droplets, Gift, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';

function AguaCard() {
  const { clienteId } = useParams();
  const [cliente, setCliente] = useState(null);
  const [config, setConfig] = useState({ nome_loja: 'LJ Águas', telefone_whatsapp: '', selos_para_premio: 10 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadData();
  }, [clienteId]);

  const loadData = async () => {
    try {
      const [clienteRes, configRes] = await Promise.all([
        api.get(`/agua/clientes/${clienteId}`),
        api.get('/agua/config')
      ]);
      setCliente(clienteRes.data);
      setConfig(configRes.data);
    } catch (error) {
      setError('Cartão não encontrado');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsApp = () => {
    const telefone = config.telefone_whatsapp.replace(/\D/g, '');
    const url = `https://wa.me/55${telefone}`;
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-600 via-cyan-700 to-cyan-900 flex items-center justify-center">
        <div className="animate-pulse">
          <Droplets className="w-16 h-16 text-white/50" />
        </div>
      </div>
    );
  }

  if (error || !cliente) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-600 via-cyan-700 to-cyan-900 flex items-center justify-center p-4">
        <div className="text-center">
          <Droplets className="w-16 h-16 text-white/30 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-white mb-2">Cartão não encontrado</h1>
          <p className="text-cyan-200">Este cartão de fidelidade não existe ou foi removido.</p>
        </div>
      </div>
    );
  }

  const selosArray = Array.from({ length: config.selos_para_premio }, (_, i) => i + 1);
  const selosFaltando = config.selos_para_premio - cliente.selos;

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-600 via-cyan-700 to-cyan-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md"
      >
        {/* Card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-cyan-500 to-cyan-600 p-6 text-center">
            <div className="w-16 h-16 bg-white rounded-2xl mx-auto flex items-center justify-center mb-3 shadow-lg">
              <Droplets className="w-10 h-10 text-cyan-600" />
            </div>
            <h1 className="text-2xl font-bold text-white">{config.nome_loja}</h1>
            <p className="text-cyan-100 text-sm">Cartão Fidelidade</p>
          </div>

          {/* Content */}
          <div className="p-6">
            {/* Saudação */}
            <div className="text-center mb-6">
              <h2 className="text-xl font-semibold text-gray-800">Olá, {cliente.nome}!</h2>
              <p className="text-gray-500 text-sm">Continue comprando para ganhar sua água grátis!</p>
            </div>

            {/* Selos */}
            <div className="grid grid-cols-5 gap-2 mb-6">
              {selosArray.map((num) => (
                <motion.div
                  key={num}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: num * 0.05 }}
                  className={`aspect-square rounded-xl flex flex-col items-center justify-center text-sm font-bold transition-all ${
                    num <= cliente.selos
                      ? 'bg-gradient-to-br from-cyan-400 to-cyan-600 text-white shadow-lg'
                      : 'bg-gray-100 text-gray-400 border-2 border-dashed border-gray-200'
                  }`}
                >
                  <Droplets className={`w-5 h-5 ${num <= cliente.selos ? 'text-white' : 'text-gray-300'}`} />
                  <span className="text-xs mt-1">20L{num}</span>
                </motion.div>
              ))}
            </div>

            {/* Progress */}
            <div className="mb-6">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="text-gray-500">Seu progresso</span>
                <span className="font-bold text-cyan-600">{cliente.selos}/{config.selos_para_premio} selos</span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(cliente.selos / config.selos_para_premio) * 100}%` }}
                  transition={{ duration: 0.8, ease: 'easeOut' }}
                  className="h-full bg-gradient-to-r from-cyan-400 to-cyan-600"
                />
              </div>
            </div>

            {/* Message */}
            <div className={`rounded-xl p-4 text-center ${
              cliente.selos >= config.selos_para_premio
                ? 'bg-green-50 border border-green-200'
                : 'bg-cyan-50 border border-cyan-200'
            }`}>
              {cliente.selos >= config.selos_para_premio ? (
                <div className="flex items-center justify-center space-x-2">
                  <Gift className="w-6 h-6 text-green-600" />
                  <span className="text-green-700 font-medium">Parabéns! Você ganhou uma água grátis!</span>
                </div>
              ) : (
                <p className="text-cyan-700">
                  Faltam <span className="font-bold">{selosFaltando}</span> selos para ganhar uma água grátis!
                </p>
              )}
            </div>

            {/* WhatsApp */}
            {config.telefone_whatsapp && (
              <Button
                onClick={handleWhatsApp}
                className="w-full mt-4 bg-green-500 hover:bg-green-600 text-white"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                WhatsApp: {config.telefone_whatsapp}
              </Button>
            )}
          </div>
        </div>

        {/* Footer */}
        <p className="text-center text-cyan-200/50 text-xs mt-6">
          Sistema de Cartão Fidelidade Digital
        </p>
      </motion.div>
    </div>
  );
}

export default AguaCard;
